import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import {
  isCreditCardExpired,
  isCreditCardExpiringSoon,
} from "@/Helpers/creditCardExpiry";
import type { ReactElement } from "react";
import { CardBrandIcon } from "@freightsimple/ui-core/Display/CardBrandIcon";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { T } from "@freightsimple/ui-core/Typography/T";

interface CreditCardsDropdownProps {
  cards: Array<CreditCard> | undefined;
  selectedStripePaymentMethodId: string | undefined;
  onOptionSelected: (_: string) => void;
}

function CardRow_({ card }: { card: CreditCard }) {
  // Expiry comes from the backend as "M/YYYY" — the shared helper owns the
  // parsing and the expired-from-the-first-of-the-following-month definition.
  const expired = isCreditCardExpired(card.expiry);
  // Within one month of expiry: selectable, but warn — the charge may happen
  // days after booking, when the card could already be expired.
  const aboutToExpire = isCreditCardExpiringSoon(card.expiry, 1);

  return (
    <Row gap={2} align="start">
      <CardBrandIcon size="md" brand={card.brand} />
      <T size="base" weight="normal">
        XXXX XXXX XXXX {card.lastFourDigits}
      </T>
      {aboutToExpire ? (
        <T size="base" weight="light" color="red">
          Too close to expiry: {card.expiry}
        </T>
      ) : expired ? (
        <T size="base" weight="light" color="red">
          Expired: {card.expiry}
        </T>
      ) : (
        <T size="base" weight="light" color="secondary">
          Expiry: {card.expiry}
        </T>
      )}
    </Row>
  );
}

function BookShipmentCreditCardsDropdown(
  props: CreditCardsDropdownProps,
): ReactElement {
  const cards = props.cards ?? [];
  const cardsByValue = new Map(cards.map((c) => [c.stripePaymentMethodId, c]));
  // Expired cards stay visible (so the customer understands why their card
  // isn't usable) but can't be selected for booking (ENG-4368)
  const options = cards.map((card) => ({
    value: card.stripePaymentMethodId,
    label: `${card.brand} ending ${card.lastFourDigits} (expires ${card.expiry})`,
    disabled: isCreditCardExpired(card.expiry),
  }));

  return (
    // Every consumer sits in the Cargo-styled payment flow, which mounts no
    // presentation provider — pin the underlined look the control has always
    // had rather than falling back to the boxed admin default.
    <ControlPresentationProvider presentation="underlined">
      <Dropdown
        placeholder="Select a credit card"
        value={props.selectedStripePaymentMethodId}
        onChange={props.onOptionSelected}
        options={options}
        width="full"
        renderOption={(option) => {
          const card = cardsByValue.get(option.value);
          return card ? <CardRow_ card={card} /> : option.label;
        }}
      />
    </ControlPresentationProvider>
  );
}
export default BookShipmentCreditCardsDropdown;
